import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Loader2, Pencil, X, PiggyBank } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { AiInsightCard } from '../../components/ui/AiInsightCard';
import { Pill } from '../../components/ui/Pill';
import { formatAmount } from '../../lib/format';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useGlAccounts } from '../../hooks/useLedger';
import {
  useBudgetUtilization,
  useCreateBudget,
  useUpdateBudget,
  useDeleteBudget,
  type BudgetInput,
  type BudgetUtilization,
} from '../../hooks/useBudgets';
import { QueryError } from '../../components/ui/QueryError';
import { EmptyState } from '../../components/ui/EmptyState';
import { IntroCallout } from '../../components/ui/IntroCallout';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { useCan } from '../../hooks/useCan';

/** Tailwind fill color for the utilization bar — amber >80%, red >100%. */
function barColor(pct: number | null): string {
  if (pct == null) return 'bg-ink-300 dark:bg-ink-700';
  if (pct > 100) return 'bg-red-500';
  if (pct > 80) return 'bg-amber-500';
  return 'bg-accent-500';
}

export function BudgetsPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const { canMutate } = useCan();
  const confirm = useConfirm();
  const { data: ws } = useWorkspace(wsId);
  const baseCurrency = ws?.base_currency ?? 'JPY';

  const currentFy = new Date().getUTCFullYear();
  const [fiscalYear, setFiscalYear] = useState<number>(currentFy);

  const util = useBudgetUtilization(wsId, fiscalYear);
  const accounts = useGlAccounts(wsId);
  const create = useCreateBudget(wsId);
  const update = useUpdateBudget(wsId);
  const del = useDeleteBudget(wsId);
  const [editingId, setEditingId] = useState<string | null>(null);

  const expenseAccounts = useMemo(
    () => (accounts.data ?? []).filter((a) => a.kind === 'expense'),
    [accounts.data],
  );

  const [form, setForm] = useState<{
    glAccountCode: string;
    department: string;
    periodMonth: string;
    amount: string;
    currency: string;
    note: string;
  }>({
    glAccountCode: '',
    department: '',
    periodMonth: '',
    amount: '',
    currency: baseCurrency,
    note: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const resetForm = () =>
    setForm({ glAccountCode: '', department: '', periodMonth: '', amount: '', currency: baseCurrency, note: '' });

  const startEdit = (b: BudgetUtilization) => {
    setEditingId(b.id);
    setFormError(null);
    setForm({
      glAccountCode: b.gl_account_code ?? '',
      department: b.department ?? '',
      periodMonth: b.period_month != null ? String(b.period_month) : '',
      amount: String(b.amount_cents),
      currency: b.currency,
      note: b.note ?? '',
    });
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormError(null);
    resetForm();
  };

  const onSubmitForm = async () => {
    setFormError(null);
    const amountCents = Math.round(Number(form.amount));
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setFormError(t('budgets.amountRequired'));
      return;
    }
    const body: BudgetInput = {
      fiscalYear,
      amountCents,
      currency: (form.currency || baseCurrency).toUpperCase().slice(0, 3),
      glAccountCode: form.glAccountCode || null,
      department: form.department || null,
      periodMonth: form.periodMonth ? Number(form.periodMonth) : null,
      note: form.note || null,
    };
    try {
      if (editingId) {
        await update.mutateAsync({ id: editingId, patch: body });
        setEditingId(null);
      } else {
        await create.mutateAsync(body);
      }
      resetForm();
    } catch (err: any) {
      setFormError(err?.response?.data?.message ?? err?.message ?? t('budgets.createFailed'));
    }
  };

  const accountName = (code: string | null) =>
    expenseAccounts.find((a) => a.code === code)?.name ?? code ?? t('budgets.allAccounts');

  const monthLabel = (m: number | null) =>
    m == null ? t('budgets.annual') : t('budgets.monthN', { n: m });

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-5xl">
        <div className="flex items-center justify-between mb-1">
          <h1 className="display text-2xl font-semibold">{t('budgets.title')}</h1>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-ink-500">{t('budgets.fiscalYear')}</span>
            <input
              type="number"
              value={fiscalYear}
              onChange={(e) => setFiscalYear(Number(e.target.value) || currentFy)}
              className="w-24 px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 num"
            />
          </label>
        </div>
        <p className="text-sm text-ink-500 mb-6">{t('budgets.subtitle')}</p>

        <IntroCallout
          storageKey="budgets"
          title={t('intro.budgets.title')}
          body={t('intro.budgets.body')}
        />

        {canMutate && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="font-medium">{editingId ? t('budgets.edit') : t('budgets.new')}</div>
              {editingId && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800 dark:hover:text-ink-200"
                >
                  <X size={12} /> {t('common.cancel')}
                </button>
              )}
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Labeled label={t('budgets.account')}>
                <select
                  value={form.glAccountCode}
                  onChange={(e) => setForm({ ...form, glAccountCode: e.target.value })}
                  className={inputCls}
                >
                  <option value="">{t('budgets.allAccounts')}</option>
                  {expenseAccounts.map((a) => (
                    <option key={a.id} value={a.code}>{a.code} · {a.name}</option>
                  ))}
                </select>
              </Labeled>
              <Labeled label={t('budgets.department')}>
                <input
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  placeholder={t('budgets.departmentOptional')}
                  className={inputCls}
                />
              </Labeled>
              <Labeled label={t('budgets.month')}>
                <select
                  value={form.periodMonth}
                  onChange={(e) => setForm({ ...form, periodMonth: e.target.value })}
                  className={inputCls}
                >
                  <option value="">{t('budgets.annual')}</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{t('budgets.monthN', { n: m })}</option>
                  ))}
                </select>
              </Labeled>
              <Labeled label={`${t('budgets.amount')} (${t('budgets.cents')})`}>
                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className={`${inputCls} num`}
                />
              </Labeled>
              <Labeled label={t('budgets.currency')}>
                <input
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase().slice(0, 3) })}
                  className={`${inputCls} num`}
                />
              </Labeled>
              <Labeled label={t('budgets.note')}>
                <input
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className={inputCls}
                />
              </Labeled>
            </div>
            {formError && <div className="mt-3 text-sm text-warn-600">{formError}</div>}
            <div className="mt-4">
              <button
                type="button"
                onClick={onSubmitForm}
                disabled={create.isPending || update.isPending}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent-600 text-white text-sm font-medium hover:bg-accent-700 disabled:opacity-50"
              >
                {create.isPending || update.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : editingId ? (
                  <Pencil size={14} />
                ) : (
                  <Plus size={14} />
                )}
                {editingId ? t('common.save') : t('budgets.new')}
              </button>
            </div>
          </CardBody>
        </Card>
        )}

        <div className="mb-6">
          <AiInsightCard
            agent="cost_reduction"
            title={t('aiOutput.cards.budgetCoach')}
            prompt={t('aiOutput.prompts.budgetCoach', { year: fiscalYear })}
            tone="recommendation"
          />
        </div>

        <Card>
          <CardHeader><div className="font-medium">{t('budgets.utilization')}</div></CardHeader>
          {util.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : util.isError ? (
            <QueryError onRetry={util.refetch} />
          ) : (util.data?.length ?? 0) === 0 ? (
            <EmptyState
              icon={PiggyBank}
              title={t('emptyState.budgets.title')}
              description={t('emptyState.budgets.body')}
              offerSampleData
              wsId={wsId}
            />
          ) : (
            <div className="divide-y divide-ink-200 dark:divide-ink-800">
              {util.data!.map((b) => (
                <BudgetRow
                  key={b.id}
                  b={b}
                  locale={i18n.language}
                  accountName={accountName(b.gl_account_code)}
                  monthLabel={monthLabel(b.period_month)}
                  onEdit={() => startEdit(b)}
                  editing={editingId === b.id}
                  onDelete={async () => {
                    const ok = await confirm({ title: t('budgets.confirmDelete'), confirmLabel: t('common.delete'), destructive: true });
                    if (ok) del.mutate(b.id);
                  }}
                  deleting={del.isPending}
                  canMutate={canMutate}
                />
              ))}
            </div>
          )}
        </Card>
      </div>
    </AppShell>
  );
}

const inputCls =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-ink-500">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

function BudgetRow({
  b,
  locale,
  accountName,
  monthLabel,
  onEdit,
  editing,
  onDelete,
  deleting,
  canMutate,
}: {
  b: BudgetUtilization;
  locale: string;
  accountName: string;
  monthLabel: string;
  onEdit: () => void;
  editing: boolean;
  onDelete: () => void;
  deleting: boolean;
  canMutate: boolean;
}) {
  const { t } = useTranslation();
  const pct = b.utilizationPct;
  const widthPct = Math.min(100, Math.max(0, pct ?? 0));
  return (
    <div className={`px-5 py-4 ${editing ? 'bg-accent-50/40 dark:bg-accent-900/15' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium truncate">{accountName}</div>
          <div className="text-xs text-ink-500 flex items-center gap-2 mt-0.5">
            <span>{monthLabel}</span>
            {b.department && <Pill tone="neutral">{b.department}</Pill>}
            {b.overBudget && <Pill tone="warn">{t('budgets.overBudget')}</Pill>}
          </div>
        </div>
        {canMutate && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={onEdit}
              aria-label={t('budgets.edit')}
              className="p-1.5 rounded-md text-ink-400 hover:text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-900/20"
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              aria-label={t('common.delete')}
              className="p-1.5 rounded-md text-ink-400 hover:text-warn-600 hover:bg-warn-50 dark:hover:bg-warn-900/20 disabled:opacity-50"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      <div className="mt-3">
        <div className="h-2 rounded-full bg-ink-100 dark:bg-ink-800 overflow-hidden">
          <div
            className={`h-full ${barColor(pct)}`}
            style={{ width: `${widthPct}%` }}
            role="progressbar"
            aria-valuenow={pct ?? 0}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-xs">
          <span className="text-ink-500">
            {t('budgets.actual')}: <span className="num text-ink-700 dark:text-ink-200">{formatAmount(b.actualCents, b.currency, locale)}</span>
            {' / '}
            <span className="num">{formatAmount(b.amount_cents, b.currency, locale)}</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="num font-medium">{pct == null ? '—' : `${pct}%`}</span>
            <span className="text-ink-500">
              {t('budgets.remaining')}: <span className="num">{formatAmount(b.remainingCents, b.currency, locale)}</span>
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
